import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { backendURL } from "../config";
import {
    Shield, ShieldCheck, ShieldAlert, Cpu, Server, Monitor,
    Laptop, Users, RefreshCw, Search, CheckCircle2, AlertTriangle,
    ExternalLink, Lock, Eye, Key, Radio, Layers, Activity,
    FileText, ArrowUpRight, ChevronRight, X, Clock, Sliders, Check,
    LayoutGrid, List, Zap, Ban, PlayCircle, Ticket, AlertOctagon,
    Terminal, Bug, Trash2, ArrowRight, CornerDownRight, ShieldX,
    CheckCircle, RotateCcw, Globe, Building2, User, HardDrive, Info,
    AlertCircle, FileCode, Hash, Navigation
} from "lucide-react";

export default function ThreatDownEDR() {
    const [status, setStatus] = useState(null);
    const [sites, setSites] = useState([]);
    const [users, setUsers] = useState([]);
    const [policies, setPolicies] = useState([]);
    const [detections, setDetections] = useState([]);
    const [detectionStats, setDetectionStats] = useState({ total: 0, active: 0, remediated: 0, isolated: 0 });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [activeTab, setActiveTab] = useState("detections"); // 'detections', 'sites', 'policies', 'users', 'actions'
    const [viewMode, setViewMode] = useState("grid"); // 'grid' | 'table'
    const [selectedSite, setSelectedSite] = useState(null);
    const [selectedDetection, setSelectedDetection] = useState(null);
    const [lastSync, setLastSync] = useState(null);

    // Filters for Detections
    const [severityFilter, setSeverityFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [siteFilter, setSiteFilter] = useState("all");

    // Action execution states
    const [remediatingId, setRemediatingId] = useState(null);
    const [actionToast, setActionToast] = useState(null);
    const [showApiAccessModal, setShowApiAccessModal] = useState(false);
    const [detailTab, setDetailTab] = useState("all"); // 'all' | 'origin' | 'why' | 'endpoint' | 'remediation'

    // Reporting modal state
    const [showReportModal, setShowReportModal] = useState(false);
    const [submittingReport, setSubmittingReport] = useState(false);
    const [reportForm, setReportForm] = useState({
        threatName: "",
        category: "Ransomware",
        severity: "critical",
        siteName: "",
        siteId: "",
        tenantLocation: "HQ Production Network",
        endpointName: "",
        endpointIp: "",
        macAddress: "00:50:56:A1:B2:C3",
        os: "Windows 11 Pro 23H2 (Build 22631)",
        loggedUser: "",
        userDomain: "",
        userRole: "Standard Corporate User",
        logonType: "Interactive (Logon Type 2)",
        sourceIp: "185.220.101.5",
        sourcePort: "443",
        sourceCountry: "External Internet / WAN",
        attackVector: "Phishing Email Attachment (Outlook)",
        referrerUrl: "",
        triggerReason: "Behavioral heuristic anomaly: suspicious binary execution and shadow copy manipulation",
        mitreTechnique: "T1486 (Data Encrypted for Impact)",
        mitreTactic: "Impact",
        filePath: "",
        process: "",
        parentProcess: "explorer.exe (PID 1824)",
        sha256: "",
        notes: ""
    });

    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const fetchData = async (force = false) => {
        if (force) setRefreshing(true);
        else setLoading(true);

        try {
            const [statusRes, sitesRes, usersRes, policiesRes, detectionsRes] = await Promise.all([
                axios.get(`${backendURL}/api/soc/integrations/threatdown/status`, { headers }),
                axios.get(`${backendURL}/api/soc/integrations/threatdown/sites${force ? "?refresh=true" : ""}`, { headers }),
                axios.get(`${backendURL}/api/soc/integrations/threatdown/users`, { headers }),
                axios.get(`${backendURL}/api/soc/integrations/threatdown/policies`, { headers }),
                axios.get(`${backendURL}/api/soc/integrations/threatdown/detections${force ? "?refresh=true" : ""}`, { headers })
            ]);

            setStatus(statusRes.data);
            setSites(sitesRes.data.sites || []);
            setUsers(usersRes.data.users || []);
            setPolicies(policiesRes.data.policies || []);
            setDetections(detectionsRes.data.detections || []);
            setDetectionStats(detectionsRes.data.stats || { total: 0, active: 0, remediated: 0, isolated: 0 });
            setLastSync(new Date());
        } catch (err) {
            console.error("Failed to load ThreatDown OneView telemetry:", err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchData();
        const timer = setInterval(() => fetchData(false), 120000); // 2 min auto refresh
        return () => clearInterval(timer);
    }, []);

    // Remediation Execution Handler
    const handleRemediate = async (detectionId, action, notes = "") => {
        setRemediatingId(`${detectionId}_${action}`);
        try {
            const res = await axios.post(
                `${backendURL}/api/soc/integrations/threatdown/remediate`,
                { detectionId, action, notes },
                { headers }
            );

            if (res.data.success) {
                setActionToast({
                    type: "success",
                    message: res.data.message || `Action '${action}' executed successfully.`
                });

                // Update detection locally immediately
                if (res.data.detection) {
                    setDetections(prev => prev.map(d => d.id === detectionId ? res.data.detection : d));
                    setSelectedDetection(res.data.detection);
                }

                // Refresh stats
                fetchData(false);
            } else {
                setActionToast({
                    type: "error",
                    message: res.data.error || "Action execution failed."
                });
            }
        } catch (err) {
            setActionToast({
                type: "error",
                message: err.response?.data?.error || err.message || "Remediation request failed."
            });
        } finally {
            setRemediatingId(null);
            setTimeout(() => setActionToast(null), 5000);
        }
    };

    // Bulk Remediation Execution Handler
    const handleBulkRemediate = async (siteId = null) => {
        setRemediatingId("bulk");
        try {
            const res = await axios.post(
                `${backendURL}/api/soc/integrations/threatdown/bulk-remediate`,
                { siteId },
                { headers }
            );

            if (res.data.success) {
                setActionToast({
                    type: "success",
                    message: res.data.message || "Bulk remediation executed across all active threats."
                });
                fetchData(true);
            }
        } catch (err) {
            setActionToast({
                type: "error",
                message: err.response?.data?.error || err.message || "Bulk remediation failed."
            });
        } finally {
            setRemediatingId(null);
            setTimeout(() => setActionToast(null), 5000);
        }
    };

    // Report / Ingest Detection Handler
    const handleCreateReport = async (e) => {
        e.preventDefault();
        if (!reportForm.threatName.trim() || !reportForm.siteName.trim()) {
            setActionToast({ type: "error", message: "Threat Name and Target Customer Site are required." });
            return;
        }

        setSubmittingReport(true);
        try {
            const res = await axios.post(
                `${backendURL}/api/soc/integrations/threatdown/report`,
                reportForm,
                { headers }
            );

            if (res.data.success) {
                setActionToast({
                    type: "success",
                    message: `Threat '${reportForm.threatName}' reported and queued for remediation.`
                });
                setShowReportModal(false);
                setReportForm({
                    threatName: "",
                    category: "Ransomware",
                    severity: "critical",
                    siteName: "",
                    siteId: "",
                    tenantLocation: "HQ Production Network",
                    endpointName: "",
                    endpointIp: "",
                    macAddress: "00:50:56:A1:B2:C3",
                    os: "Windows 11 Pro 23H2 (Build 22631)",
                    loggedUser: "",
                    userDomain: "",
                    userRole: "Standard Corporate User",
                    logonType: "Interactive (Logon Type 2)",
                    sourceIp: "185.220.101.5",
                    sourcePort: "443",
                    sourceCountry: "External Internet / WAN",
                    attackVector: "Phishing Email Attachment (Outlook)",
                    referrerUrl: "",
                    triggerReason: "Behavioral heuristic anomaly: suspicious binary execution and shadow copy manipulation",
                    mitreTechnique: "T1486 (Data Encrypted for Impact)",
                    mitreTactic: "Impact",
                    filePath: "",
                    process: "",
                    parentProcess: "explorer.exe (PID 1824)",
                    sha256: "",
                    notes: ""
                });
                fetchData(true);
            } else {
                setActionToast({ type: "error", message: res.data.error || "Failed to report detection." });
            }
        } catch (err) {
            setActionToast({
                type: "error",
                message: err.response?.data?.error || err.message || "Failed to report detection."
            });
        } finally {
            setSubmittingReport(false);
            setTimeout(() => setActionToast(null), 5000);
        }
    };

    // Delete Detection Handler
    const handleDeleteDetection = async (detectionId) => {
        if (!window.confirm("Are you sure you want to delete this threat record?")) return;
        try {
            const res = await axios.delete(
                `${backendURL}/api/soc/integrations/threatdown/detections/${detectionId}`,
                { headers }
            );
            if (res.data.success) {
                setDetections(prev => prev.filter(d => d.id !== detectionId));
                if (selectedDetection?.id === detectionId) setSelectedDetection(null);
                fetchData(false);
                setActionToast({ type: "success", message: "Threat detection record removed." });
            }
        } catch (err) {
            setActionToast({ type: "error", message: err.response?.data?.error || err.message });
        } finally {
            setTimeout(() => setActionToast(null), 4000);
        }
    };

    // Filtered Detections
    const filteredDetections = useMemo(() => {
        return detections.filter(d => {
            // Search query filter
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                const matches = (
                    (d.threatName || "").toLowerCase().includes(q) ||
                    (d.endpointName || "").toLowerCase().includes(q) ||
                    (d.endpointIp || "").toLowerCase().includes(q) ||
                    (d.siteName || "").toLowerCase().includes(q) ||
                    (d.filePath || "").toLowerCase().includes(q) ||
                    (d.category || "").toLowerCase().includes(q)
                );
                if (!matches) return false;
            }

            // Severity filter
            if (severityFilter !== "all" && d.severity !== severityFilter) return false;

            // Status filter
            if (statusFilter === "active" && d.status !== "active") return false;
            if (statusFilter === "remediated" && d.status !== "remediated" && d.status !== "quarantined") return false;
            if (statusFilter === "isolated" && !d.endpointIsolated) return false;

            // Site filter
            if (siteFilter !== "all" && d.siteName !== siteFilter) return false;

            return true;
        });
    }, [detections, searchQuery, severityFilter, statusFilter, siteFilter]);

    // Filter sites
    const filteredSites = useMemo(() => {
        if (!searchQuery.trim()) return sites;
        const q = searchQuery.toLowerCase();
        return sites.filter(s =>
            (s.companyName || "").toLowerCase().includes(q) ||
            (s.contactEmail || "").toLowerCase().includes(q) ||
            (s.activeProducts || []).some(p => p.toLowerCase().includes(q)) ||
            (s.admins || []).some(a => a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q))
        );
    }, [sites, searchQuery]);

    const totalEndpointsProtected = useMemo(() => {
        return sites.reduce((sum, s) => sum + (s.totalEndpointsUsed || 0), 0);
    }, [sites]);

    // Unique site names for detection filter dropdown
    const detectionSiteOptions = useMemo(() => {
        const set = new Set(detections.map(d => d.siteName).filter(Boolean));
        return Array.from(set);
    }, [detections]);

    return (
        <div className="w-full space-y-5 text-slate-200 pb-12">
            {/* ── Top Toast Notification ── */}
            {actionToast && (
                <div className={`w-full p-3.5 rounded-xl flex items-center justify-between text-xs font-medium border backdrop-blur-md animate-in fade-in transition-all ${
                    actionToast.type === "success"
                        ? "bg-emerald-950/70 border-emerald-500/40 text-emerald-300"
                        : "bg-red-950/70 border-red-500/40 text-red-300"
                }`}>
                    <div className="flex items-center gap-2.5">
                        {actionToast.type === "success" ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                        ) : (
                            <AlertOctagon className="w-4 h-4 text-red-400 flex-shrink-0" />
                        )}
                        <span>{actionToast.message}</span>
                    </div>
                    <button onClick={() => setActionToast(null)} className="text-slate-400 hover:text-white">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* ── Top Header & Integration Banner (Edge to Edge) ── */}
            <div className="w-full flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-slate-900/80 border border-white/[0.08] p-6 rounded-2xl backdrop-blur-xl relative overflow-hidden shadow-2xl">
                <div className="absolute -right-20 -top-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 via-indigo-600 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/20 flex-shrink-0">
                        <ShieldCheck className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="text-xl font-bold tracking-tight text-white">ThreatDown OneView</h1>
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                                Malwarebytes EDR
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                <Check className="w-3 h-3" /> Live Telemetry Ingestion
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-500/15 text-rose-300 border border-rose-500/30">
                                <Zap className="w-3 h-3 text-rose-400" /> One-Click Remediation Active
                            </span>
                        </div>
                        <p className="text-sm text-slate-400 mt-1">
                            Autonomous Endpoint Detection & Response telemetry, live threat reporting, host containment, and one-click remediation across managed organizations.
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                    <div className="px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-xs text-slate-400 flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-slate-500" />
                        <span>Synced: {lastSync ? lastSync.toLocaleTimeString() : "Pending"}</span>
                    </div>

                    <button
                        onClick={() => fetchData(true)}
                        disabled={refreshing || loading}
                        className="flex items-center gap-2 px-3.5 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                        <span>{refreshing ? "Syncing..." : "Sync ThreatDown"}</span>
                    </button>
                </div>
            </div>

            {/* ── Mode & Scope Alert ── */}
            <div className="w-full flex flex-col md:flex-row items-start md:items-center justify-between gap-4 px-4 py-3 rounded-xl bg-gradient-to-r from-blue-950/60 via-slate-900/60 to-indigo-950/60 border border-blue-500/20 text-xs">
                <div className="flex items-center gap-2.5 text-blue-300">
                    <Shield className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <span>
                        <strong>Unified SOAR Active Mode:</strong> QueryTel Sentinel correlates live telemetry across all {sites.length} managed client organizations. You can <strong>Remediate, Isolate hosts, or Quarantine threats directly from this console</strong> with complete forensic accountability.
                    </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                    <button
                        onClick={() => setShowApiAccessModal(true)}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 border border-blue-500/30 text-[11px] font-semibold transition-all"
                        title="Click to view API scopes, cloud agent commands, and remediation access architecture"
                    >
                        <Info className="w-3.5 h-3.5 text-blue-400" />
                        <span>API Access: OneView (Full Read/Write/Execute) + SOAR Engine</span>
                    </button>
                    <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20 whitespace-nowrap">
                        Remediation: ENABLED
                    </span>
                </div>
            </div>

            {/* ── Metric KPI Cards (Full Width Grid) ── */}
            <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-4 gap-4">
                {/* 1. Reported Threats (Clickable to switch tab) */}
                <div
                    onClick={() => setActiveTab("detections")}
                    className={`p-5 rounded-xl border transition-all cursor-pointer relative overflow-hidden backdrop-blur-sm ${
                        detectionStats.active > 0
                            ? "bg-rose-950/40 border-rose-500/30 hover:border-rose-500/50 shadow-lg shadow-rose-950/30"
                            : "bg-slate-900/60 border-white/[0.06] hover:border-white/[0.12]"
                    }`}
                >
                    <div className="flex items-center justify-between text-slate-400 mb-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-rose-300">
                            Reported Threats
                        </span>
                        <AlertOctagon className={`w-4 h-4 ${detectionStats.active > 0 ? "text-rose-400 animate-pulse" : "text-slate-500"}`} />
                    </div>
                    <div className="flex items-baseline gap-2">
                        <div className={`text-2xl font-black ${detectionStats.active > 0 ? "text-rose-400" : "text-white"}`}>
                            {detectionStats.active}
                        </div>
                        <span className="text-xs text-slate-400">active pending action</span>
                    </div>
                    <div className="mt-2 text-[11px] flex items-center justify-between border-t border-white/[0.06] pt-2">
                        <span className="text-emerald-400 font-medium">✓ {detectionStats.remediated} Remediated</span>
                        <span className="text-rose-300 font-semibold flex items-center gap-1 hover:underline">
                            Remediate <ArrowRight className="w-3 h-3" />
                        </span>
                    </div>
                </div>

                {/* 2. Managed Sites */}
                <div
                    onClick={() => setActiveTab("sites")}
                    className="p-5 rounded-xl bg-slate-900/60 border border-white/[0.06] hover:border-white/[0.12] transition-all cursor-pointer backdrop-blur-sm"
                >
                    <div className="flex items-center justify-between text-slate-400 mb-2">
                        <span className="text-xs font-medium uppercase tracking-wider">Managed Sites</span>
                        <Server className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="text-2xl font-bold text-white">{sites.length}</div>
                    <p className="text-[11px] text-slate-500 mt-1">Live Connected via ThreatDown API</p>
                </div>

                {/* 3. Protected Endpoints & Isolated Hosts */}
                <div className="p-5 rounded-xl bg-slate-900/60 border border-white/[0.06] backdrop-blur-sm">
                    <div className="flex items-center justify-between text-slate-400 mb-2">
                        <span className="text-xs font-medium uppercase tracking-wider">Active EDR Endpoints</span>
                        <Monitor className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div className="flex items-baseline gap-2">
                        <div className="text-2xl font-bold text-emerald-400">{totalEndpointsProtected}</div>
                        {detectionStats.isolated > 0 && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30">
                                🚫 {detectionStats.isolated} Isolated
                            </span>
                        )}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">Workstations & Servers Across Fleet</p>
                </div>

                {/* 4. Fleet Security Policies */}
                <div
                    onClick={() => setActiveTab("policies")}
                    className="p-5 rounded-xl bg-slate-900/60 border border-white/[0.06] hover:border-white/[0.12] transition-all cursor-pointer backdrop-blur-sm"
                >
                    <div className="flex items-center justify-between text-slate-400 mb-2">
                        <span className="text-xs font-medium uppercase tracking-wider">Fleet Security Policies</span>
                        <Lock className="w-4 h-4 text-purple-400" />
                    </div>
                    <div className="text-2xl font-bold text-purple-400">{policies.length}</div>
                    <p className="text-[11px] text-slate-500 mt-1">Tamper Protection & Ransomware RTP</p>
                </div>
            </div>

            {/* ── Tabs & View Options (Full Width) ── */}
            <div className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-white/[0.06] pb-3">
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Primary Detections Tab with Alert Badge */}
                    <button
                        onClick={() => setActiveTab("detections")}
                        className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            activeTab === "detections"
                                ? "bg-rose-600 text-white shadow-lg shadow-rose-600/30"
                                : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
                        }`}
                    >
                        <ShieldAlert className="w-3.5 h-3.5" />
                        <span>Reported Threats & Remediation</span>
                        {detectionStats.active > 0 && (
                            <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-white text-rose-600 animate-pulse">
                                {detectionStats.active}
                            </span>
                        )}
                    </button>

                    <button
                        onClick={() => setActiveTab("sites")}
                        className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            activeTab === "sites"
                                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                                : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
                        }`}
                    >
                        Managed Sites ({sites.length})
                    </button>
                    <button
                        onClick={() => setActiveTab("policies")}
                        className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            activeTab === "policies"
                                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                                : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
                        }`}
                    >
                        Security Policies ({policies.length})
                    </button>
                    <button
                        onClick={() => setActiveTab("users")}
                        className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            activeTab === "users"
                                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                                : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
                        }`}
                    >
                        User Directory ({users.length})
                    </button>
                    <button
                        onClick={() => setActiveTab("actions")}
                        className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            activeTab === "actions"
                                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                                : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
                        }`}
                    >
                        Autonomous SOAR Actions
                    </button>
                </div>

                {/* Search Bar */}
                <div className="flex items-center gap-3 w-full sm:w-auto">
                    <div className="relative w-full sm:w-80">
                        <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder={activeTab === "detections" ? "Filter threats, hosts, file paths..." : "Filter sites, subscriptions, admins..."}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-slate-900/60 border border-white/[0.08] rounded-lg pl-9 pr-3 py-1.5 text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-blue-500/50"
                        />
                    </div>

                    {activeTab === "sites" && (
                        <div className="flex items-center p-0.5 rounded-lg bg-slate-900/80 border border-white/[0.08]">
                            <button
                                onClick={() => setViewMode("grid")}
                                title="Card Grid View"
                                className={`p-1.5 rounded ${viewMode === "grid" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}
                            >
                                <LayoutGrid className="w-3.5 h-3.5" />
                            </button>
                            <button
                                onClick={() => setViewMode("table")}
                                title="Enterprise Table View"
                                className={`p-1.5 rounded ${viewMode === "table" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}
                            >
                                <List className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* ── TAB CONTENT ── */}
            {loading ? (
                <div className="py-24 flex flex-col items-center justify-center text-slate-500 gap-3">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                    <p className="text-xs">Querying ThreatDown OneView API & Telemetry Engine...</p>
                </div>
            ) : (
                <>
                    {/* ══════════════════════════════════════════════════════════════
                        TAB 1: REPORTED THREATS & REMEDIATION (CORE FEATURE)
                    ══════════════════════════════════════════════════════════════ */}
                    {activeTab === "detections" && (
                        <div className="space-y-4">
                            {/* Sub-Filters & Bulk Remediation Header */}
                            <div className="w-full flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 rounded-xl bg-slate-900/70 border border-white/[0.06] backdrop-blur-sm">
                                <div className="flex items-center gap-2 flex-wrap text-xs">
                                    <span className="text-slate-400 font-medium">Filter:</span>
                                    {/* Severity Filter */}
                                    <select
                                        value={severityFilter}
                                        onChange={(e) => setSeverityFilter(e.target.value)}
                                        className="bg-slate-800 border border-white/[0.08] rounded-lg px-2.5 py-1 text-slate-200 focus:outline-none"
                                    >
                                        <option value="all">All Severities</option>
                                        <option value="critical">Critical</option>
                                        <option value="high">High</option>
                                        <option value="medium">Medium</option>
                                        <option value="low">Low</option>
                                    </select>

                                    {/* Status Filter */}
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="bg-slate-800 border border-white/[0.08] rounded-lg px-2.5 py-1 text-slate-200 focus:outline-none"
                                    >
                                        <option value="all">All Statuses</option>
                                        <option value="active">Active (Needs Action)</option>
                                        <option value="remediated">Remediated / Cleaned</option>
                                        <option value="isolated">Host Isolated</option>
                                    </select>

                                    {/* Site Filter */}
                                    <select
                                        value={siteFilter}
                                        onChange={(e) => setSiteFilter(e.target.value)}
                                        className="bg-slate-800 border border-white/[0.08] rounded-lg px-2.5 py-1 text-slate-200 focus:outline-none"
                                    >
                                        <option value="all">All Managed Sites</option>
                                        {detectionSiteOptions.map(name => (
                                            <option key={name} value={name}>{name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Bulk Remediation & Report Action */}
                                <div className="flex items-center gap-2.5">
                                    <button
                                        onClick={() => setShowReportModal(true)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-semibold shadow-lg shadow-blue-600/20 transition-all"
                                    >
                                        <Bug className="w-3.5 h-3.5" />
                                        <span>+ Report Threat Incident</span>
                                    </button>
                                    <button
                                        onClick={() => handleBulkRemediate(null)}
                                        disabled={remediatingId === "bulk" || detectionStats.active === 0}
                                        className="flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 text-white rounded-lg text-xs font-bold shadow-lg shadow-rose-600/20 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        {remediatingId === "bulk" ? (
                                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                            <Zap className="w-3.5 h-3.5" />
                                        )}
                                        <span>Remediate All Active ({detectionStats.active})</span>
                                    </button>
                                </div>
                            </div>

                            {/* Detections List */}
                            {filteredDetections.length === 0 ? (
                                <div className="py-16 px-6 text-center rounded-2xl bg-slate-900/40 border border-white/[0.06] space-y-4 max-w-2xl mx-auto backdrop-blur-md">
                                    <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/10">
                                        <ShieldCheck className="w-8 h-8" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <h3 className="text-base font-bold text-white tracking-tight">Zero Active Threats Reported</h3>
                                        <p className="text-xs text-slate-400 max-w-lg mx-auto leading-relaxed">
                                            All {totalEndpointsProtected} deployed endpoints across {sites.length} managed customer organizations are clean and protected under ThreatDown OneView real-time policies.
                                        </p>
                                    </div>
                                    <div className="pt-2 flex items-center justify-center gap-3">
                                        <button
                                            onClick={() => setShowReportModal(true)}
                                            className="flex items-center gap-2 px-3.5 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30 rounded-lg text-xs font-semibold transition-all"
                                        >
                                            <Bug className="w-3.5 h-3.5" />
                                            <span>+ Report Threat / Incident</span>
                                        </button>
                                        <button
                                            onClick={() => fetchData(true)}
                                            className="flex items-center gap-2 px-3.5 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 border border-white/[0.08] rounded-lg text-xs font-medium transition-all"
                                        >
                                            <RefreshCw className="w-3.5 h-3.5" />
                                            <span>Verify Live Status</span>
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {filteredDetections.map((det) => {
                                        const isRemediatingThis = remediatingId?.startsWith(det.id);
                                        const isActionActive = det.status === "active";
                                        const isIsolated = det.endpointIsolated;

                                        return (
                                            <div
                                                key={det.id}
                                                className={`p-5 rounded-xl border transition-all backdrop-blur-sm relative overflow-hidden ${
                                                    isActionActive
                                                        ? "bg-slate-900/80 border-rose-500/30 hover:border-rose-500/50 shadow-lg shadow-rose-950/20"
                                                        : isIsolated
                                                        ? "bg-slate-900/80 border-amber-500/30 hover:border-amber-500/50 shadow-lg shadow-amber-950/20"
                                                        : "bg-slate-900/60 border-white/[0.06] hover:border-white/[0.12]"
                                                }`}
                                            >
                                                {/* Left Accent Bar */}
                                                <div className={`absolute left-0 top-0 bottom-0 w-1 ${
                                                    det.severity === "critical"
                                                        ? "bg-rose-500"
                                                        : det.severity === "high"
                                                        ? "bg-orange-500"
                                                        : det.severity === "medium"
                                                        ? "bg-amber-500"
                                                        : "bg-blue-500"
                                                }`} />

                                                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                                    {/* Threat Metadata */}
                                                    <div className="space-y-2 flex-1">
                                                        <div className="flex items-center gap-2.5 flex-wrap">
                                                            {/* Severity Badge */}
                                                            <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${
                                                                det.severity === "critical"
                                                                    ? "bg-rose-500/20 text-rose-300 border border-rose-500/30"
                                                                    : det.severity === "high"
                                                                    ? "bg-orange-500/20 text-orange-300 border border-orange-500/30"
                                                                    : det.severity === "medium"
                                                                    ? "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                                                                    : "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                                                            }`}>
                                                                {det.severity}
                                                            </span>

                                                            {/* Threat Name */}
                                                            <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                                                {det.threatName}
                                                            </h3>

                                                            {/* Category */}
                                                            <span className="px-2 py-0.5 rounded bg-white/[0.04] text-slate-300 text-[10px] border border-white/[0.06]">
                                                                {det.category}
                                                            </span>

                                                            {/* MITRE Technique */}
                                                            {det.mitreTechnique && (
                                                                <span className="px-2 py-0.5 rounded bg-purple-500/10 text-purple-300 text-[10px] font-mono border border-purple-500/20">
                                                                    {det.mitreTechnique}
                                                                </span>
                                                            )}

                                                            {/* Live Status Badge */}
                                                            {det.status === "active" ? (
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-rose-500/15 text-rose-300 border border-rose-500/30 animate-pulse">
                                                                    <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                                                                    Action Required
                                                                </span>
                                                            ) : det.status === "quarantined" ? (
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30">
                                                                    <Lock className="w-3 h-3 text-amber-400" />
                                                                    Quarantined
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                                                                    <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                                                                    Remediated & Clean
                                                                </span>
                                                            )}

                                                            {isIsolated && (
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30">
                                                                    <Ban className="w-3 h-3 text-purple-400" />
                                                                    Host Isolated
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* 5-Pillar Forensic Telemetry Chips */}
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 pt-1 text-xs">
                                                            {/* 1. Origin / Where it comes from */}
                                                            <div className="p-2.5 rounded-lg bg-black/40 border border-white/[0.04] space-y-1">
                                                                <span className="text-[10px] uppercase font-bold tracking-wider text-cyan-400 flex items-center gap-1">
                                                                    <Globe className="w-3 h-3 text-cyan-400" /> Origin / Where
                                                                </span>
                                                                <div className="font-mono text-[11px] text-cyan-200 truncate font-semibold">
                                                                    {det.sourceIp || "185.220.101.5"}
                                                                </div>
                                                                <div className="text-[10px] text-slate-400 truncate">
                                                                    {det.attackVector || "Phishing Email Ingress"}
                                                                </div>
                                                            </div>

                                                            {/* 2. Why it comes / Trigger Reason */}
                                                            <div className="p-2.5 rounded-lg bg-black/40 border border-white/[0.04] space-y-1">
                                                                <span className="text-[10px] uppercase font-bold tracking-wider text-amber-400 flex items-center gap-1">
                                                                    <AlertTriangle className="w-3 h-3 text-amber-400" /> Why Triggered
                                                                </span>
                                                                <div className="text-[11px] text-amber-200 font-medium truncate" title={det.triggerReason}>
                                                                    {det.triggerReason || det.category || "Heuristic anomaly detected"}
                                                                </div>
                                                                <div className="text-[10px] text-purple-300 font-mono truncate">
                                                                    MITRE: {det.mitreTechnique || "T1486 / T1059"}
                                                                </div>
                                                            </div>

                                                            {/* 3. Site & PC / Computer */}
                                                            <div className="p-2.5 rounded-lg bg-black/40 border border-white/[0.04] space-y-1">
                                                                <span className="text-[10px] uppercase font-bold tracking-wider text-blue-400 flex items-center gap-1">
                                                                    <Building2 className="w-3 h-3 text-blue-400" /> Site & PC
                                                                </span>
                                                                <div className="text-[11px] text-white font-semibold truncate">
                                                                    {det.siteName}
                                                                </div>
                                                                <div className="text-[10px] text-slate-300 font-mono truncate flex items-center gap-1">
                                                                    <Monitor className="w-2.5 h-2.5 text-slate-400" />
                                                                    <span>{det.endpointName}</span>
                                                                    <span className="text-slate-500">({det.endpointIp})</span>
                                                                </div>
                                                            </div>

                                                            {/* 4. Affected User */}
                                                            <div className="p-2.5 rounded-lg bg-black/40 border border-white/[0.04] space-y-1">
                                                                <span className="text-[10px] uppercase font-bold tracking-wider text-emerald-400 flex items-center gap-1">
                                                                    <User className="w-3 h-3 text-emerald-400" /> Affected User
                                                                </span>
                                                                <div className="text-[11px] text-emerald-300 font-mono truncate font-medium">
                                                                    {det.userDomain || (det.loggedUser ? `CORP\\${det.loggedUser}` : "CORP\\User")}
                                                                </div>
                                                                <div className="text-[10px] text-slate-400 truncate">
                                                                    {det.userRole || "Interactive Enterprise User"}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* File / Process Location */}
                                                        {det.filePath && (
                                                            <div className="flex items-center gap-2 p-2 rounded-lg bg-black/40 border border-white/[0.04] text-[11px] font-mono text-slate-300 max-w-4xl overflow-x-auto">
                                                                <Terminal className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                                                                <span className="truncate text-rose-300/90">{det.filePath}</span>
                                                                {det.process && (
                                                                    <span className="text-slate-500 text-[10px] truncate">&bull; Process: {det.process}</span>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Remediated Details if applicable */}
                                                        {det.remediated && det.remediatedBy && (
                                                            <div className="p-2 rounded-lg bg-emerald-950/40 border border-emerald-500/20 text-[11px] text-emerald-300 flex items-center justify-between flex-wrap gap-2">
                                                                <div className="flex items-center gap-1.5">
                                                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                                                    <span>Remediated by <strong>{det.remediatedBy}</strong> on {new Date(det.remediatedAt || Date.now()).toLocaleString()}</span>
                                                                </div>
                                                                <span className="text-[10px] font-mono text-emerald-400/80 bg-emerald-500/10 px-2 py-0.5 rounded">
                                                                    SOAR Quarantine & Host Isolation Verified
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* ── Action Control Panel (The Remediation Tool) ── */}
                                                    <div className="flex items-center gap-2 flex-wrap lg:flex-nowrap justify-end flex-shrink-0">
                                                        {/* 1. Primary Remediation Button */}
                                                        {!det.remediated ? (
                                                            <button
                                                                onClick={() => handleRemediate(det.id, "remediate")}
                                                                disabled={isRemediatingThis}
                                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-lg text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all disabled:opacity-50"
                                                                title="Remediate & clean threat payload from endpoint"
                                                            >
                                                                {remediatingId === `${det.id}_remediate` ? (
                                                                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                                ) : (
                                                                    <ShieldCheck className="w-3.5 h-3.5" />
                                                                )}
                                                                <span>Remediate Threat</span>
                                                            </button>
                                                        ) : (
                                                            <div className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg text-xs font-semibold">
                                                                <CheckCircle className="w-3.5 h-3.5" />
                                                                <span>Remediated</span>
                                                            </div>
                                                        )}

                                                        {/* 2. Network Isolation / De-isolation Button */}
                                                        {!isIsolated ? (
                                                            <button
                                                                onClick={() => handleRemediate(det.id, "isolate")}
                                                                disabled={isRemediatingThis}
                                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-600/20 hover:bg-rose-600/30 text-rose-300 border border-rose-500/30 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                                                                title="Cut off host from corporate network to block lateral spread"
                                                            >
                                                                {remediatingId === `${det.id}_isolate` ? (
                                                                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                                ) : (
                                                                    <Ban className="w-3.5 h-3.5 text-rose-400" />
                                                                )}
                                                                <span>Isolate Host</span>
                                                            </button>
                                                        ) : (
                                                            <button
                                                                onClick={() => handleRemediate(det.id, "deisolate")}
                                                                disabled={isRemediatingThis}
                                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-500/30 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                                                                title="Restore regular network connectivity"
                                                            >
                                                                {remediatingId === `${det.id}_deisolate` ? (
                                                                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                                ) : (
                                                                    <RotateCcw className="w-3.5 h-3.5 text-purple-400" />
                                                                )}
                                                                <span>Restore Network</span>
                                                            </button>
                                                        )}

                                                        {/* 3. Deep Scan Button */}
                                                        <button
                                                            onClick={() => handleRemediate(det.id, "scan")}
                                                            disabled={isRemediatingThis}
                                                            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 border border-white/[0.08] rounded-lg text-xs font-medium transition-all"
                                                            title="Dispatch deep ThreatDown signature scan"
                                                        >
                                                            {remediatingId === `${det.id}_scan` ? (
                                                                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                            ) : (
                                                                <PlayCircle className="w-3.5 h-3.5 text-blue-400" />
                                                            )}
                                                            <span>Deep Scan</span>
                                                        </button>

                                                        {/* 4. Escalate Ticket Button */}
                                                        <button
                                                            onClick={() => handleRemediate(det.id, "ticket")}
                                                            disabled={isRemediatingThis}
                                                            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 border border-white/[0.08] rounded-lg text-xs font-medium transition-all"
                                                            title="Dispatch high-priority Zoho Desk SOC ticket"
                                                        >
                                                            {remediatingId === `${det.id}_ticket` ? (
                                                                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                            ) : (
                                                                <Ticket className="w-3.5 h-3.5 text-amber-400" />
                                                            )}
                                                            <span>Ticket</span>
                                                        </button>

                                                        {/* 5. View Evidence Modal */}
                                                        <button
                                                            onClick={() => setSelectedDetection(det)}
                                                            className="p-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-400 hover:text-white rounded-lg transition-colors border border-white/[0.08]"
                                                            title="View full forensic audit trail and evidence"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>

                                                        {/* 6. Delete Threat Record */}
                                                        <button
                                                            onClick={() => handleDeleteDetection(det.id)}
                                                            className="p-1.5 bg-white/[0.04] hover:bg-rose-500/20 text-slate-400 hover:text-rose-400 rounded-lg transition-colors border border-white/[0.08]"
                                                            title="Delete detection record"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ══════════════════════════════════════════════════════════════
                        TAB 2: MANAGED SITES
                    ══════════════════════════════════════════════════════════════ */}
                    {activeTab === "sites" && (
                        viewMode === "grid" ? (
                            <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
                                {filteredSites.map((site) => {
                                    // Check if this site has active threats
                                    const siteActiveThreats = detections.filter(
                                        d => (d.siteName === site.companyName || d.siteId === site.id) && d.status === "active"
                                    );

                                    return (
                                        <div
                                            key={site.id}
                                            className={`p-5 rounded-xl border transition-all flex flex-col justify-between group relative backdrop-blur-sm ${
                                                siteActiveThreats.length > 0
                                                    ? "bg-slate-900/80 border-rose-500/40 shadow-lg shadow-rose-950/20"
                                                    : "bg-slate-900/70 border-white/[0.06] hover:border-blue-500/30"
                                            }`}
                                        >
                                            {/* Site Active Threat Alert Banner */}
                                            {siteActiveThreats.length > 0 && (
                                                <div className="mb-3 p-2 rounded-lg bg-rose-500/15 border border-rose-500/30 text-[11px] font-bold text-rose-300 flex items-center justify-between">
                                                    <span className="flex items-center gap-1.5">
                                                        <AlertOctagon className="w-3.5 h-3.5 text-rose-400 animate-pulse" />
                                                        {siteActiveThreats.length} Threat(s) Reported
                                                    </span>
                                                    <button
                                                        onClick={() => {
                                                            setSiteFilter(site.companyName);
                                                            setActiveTab("detections");
                                                        }}
                                                        className="text-xs underline text-rose-200 hover:text-white"
                                                    >
                                                        Remediate
                                                    </button>
                                                </div>
                                            )}

                                            <div>
                                                <div className="flex items-start justify-between gap-3 mb-3">
                                                    <div>
                                                        <h3 className="font-semibold text-white group-hover:text-blue-400 transition-colors">
                                                            {site.companyName}
                                                        </h3>
                                                        <p className="text-[11px] text-slate-500 font-mono mt-0.5">
                                                            ID: {site.id.slice(0, 14)}...
                                                        </p>
                                                    </div>
                                                    <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                                        {site.accountStatus}
                                                    </span>
                                                </div>

                                                <div className="space-y-2 text-xs text-slate-400 my-3">
                                                    <div className="flex items-center justify-between py-1 border-b border-white/[0.04]">
                                                        <span className="text-slate-500">Active Endpoints:</span>
                                                        <span className={`font-mono font-semibold ${site.totalEndpointsUsed > 0 ? "text-emerald-400 font-bold" : "text-slate-400"}`}>
                                                            {site.totalEndpointsUsed} deployed
                                                        </span>
                                                    </div>

                                                    <div className="flex items-center justify-between py-1 border-b border-white/[0.04]">
                                                        <span className="text-slate-500">Contact Email:</span>
                                                        <span className="text-slate-300 font-mono text-[11px] truncate max-w-[180px]">
                                                            {site.contactEmail || "No contact email provided"}
                                                        </span>
                                                    </div>

                                                    <div className="py-1">
                                                        <span className="text-slate-500 block mb-1">Products & Subscriptions:</span>
                                                        <div className="flex flex-wrap gap-1">
                                                            {site.activeProducts?.map((prod, i) => (
                                                                <span key={i} className="px-2 py-0.5 rounded text-[10px] bg-blue-500/10 text-blue-300 border border-blue-500/20 truncate max-w-[200px]">
                                                                    {prod}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="pt-3 border-t border-white/[0.06] flex items-center justify-between text-xs">
                                                <span className="text-slate-500">{site.admins?.length || 0} Authorized Admins</span>
                                                <button
                                                    onClick={() => setSelectedSite(site)}
                                                    className="text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1 group-hover:translate-x-0.5 transition-transform"
                                                >
                                                    <span>Inspect Site</span>
                                                    <ChevronRight className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            /* Enterprise Table View */
                            <div className="w-full rounded-xl bg-slate-900/70 border border-white/[0.06] overflow-x-auto shadow-xl">
                                <table className="w-full text-left text-xs">
                                    <thead className="bg-white/[0.02] border-b border-white/[0.06] text-slate-400">
                                        <tr>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Company Name</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Status</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Endpoints</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Subscriptions</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Threat Status</th>
                                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/[0.04] text-slate-300">
                                        {filteredSites.map((site) => {
                                            const siteActiveThreats = detections.filter(
                                                d => (d.siteName === site.companyName || d.siteId === site.id) && d.status === "active"
                                            );

                                            return (
                                                <tr key={site.id} className="hover:bg-white/[0.02] transition-colors">
                                                    <td className="py-3 px-4 font-medium text-white">{site.companyName}</td>
                                                    <td className="py-3 px-4">
                                                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                                            {site.accountStatus}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-4 font-mono">{site.totalEndpointsUsed}</td>
                                                    <td className="py-3 px-4 text-slate-400">{site.activeProducts?.join(", ")}</td>
                                                    <td className="py-3 px-4">
                                                        {siteActiveThreats.length > 0 ? (
                                                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-500/20 text-rose-300 border border-rose-500/30">
                                                                🚨 {siteActiveThreats.length} Active Threat(s)
                                                            </span>
                                                        ) : (
                                                            <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-400">
                                                                Clean
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <button
                                                            onClick={() => setSelectedSite(site)}
                                                            className="text-blue-400 hover:text-blue-300 font-medium"
                                                        >
                                                            Inspect
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}

                    {/* ══════════════════════════════════════════════════════════════
                        TAB 3: POLICIES
                    ══════════════════════════════════════════════════════════════ */}
                    {activeTab === "policies" && (
                        <div className="w-full space-y-4">
                            <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {policies.map((pol) => (
                                    <div key={pol.id} className="p-5 rounded-xl bg-slate-900/70 border border-white/[0.06] space-y-3">
                                        <div className="flex items-start justify-between">
                                            <h4 className="font-semibold text-white">{pol.name}</h4>
                                            <span className="px-2 py-0.5 rounded text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                                {pol.appliedSitesCount} Sites Enforced
                                            </span>
                                        </div>

                                        <div className="space-y-1.5 text-xs text-slate-400">
                                            <div className="flex items-center justify-between">
                                                <span>Tamper Protection:</span>
                                                <span className={pol.tamperProtection ? "text-emerald-400 font-semibold" : "text-amber-400"}>
                                                    {pol.tamperProtection ? "✓ Password Locked" : "Disabled"}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span>Ransomware RTP:</span>
                                                <span className={pol.ransomwareProtection ? "text-emerald-400 font-semibold" : "text-slate-400"}>
                                                    {pol.ransomwareProtection ? "✓ Real-Time Enforced" : "Configured"}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span>Intrusion Prevention:</span>
                                                <span className="text-emerald-400 font-semibold">Active</span>
                                            </div>
                                        </div>

                                        <div className="pt-2 text-[10px] text-slate-500 flex items-center justify-between border-t border-white/[0.04]">
                                            <span>Active Modules: {pol.modulesCount}</span>
                                            <span className="text-emerald-400 font-semibold">Live Enforced</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ══════════════════════════════════════════════════════════════
                        TAB 4: USERS
                    ══════════════════════════════════════════════════════════════ */}
                    {activeTab === "users" && (
                        <div className="w-full rounded-xl bg-slate-900/70 border border-white/[0.06] overflow-x-auto shadow-xl">
                            <table className="w-full text-left text-xs">
                                <thead className="bg-white/[0.02] border-b border-white/[0.06] text-slate-400">
                                    <tr>
                                        <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">User / Name</th>
                                        <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Email</th>
                                        <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Assigned Role</th>
                                        <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Status</th>
                                        <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">2FA Authentication</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/[0.04] text-slate-300">
                                    {users.map((u) => (
                                        <tr key={u.id} className="hover:bg-white/[0.02] transition-colors">
                                            <td className="py-3 px-4 font-medium text-white flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center font-bold text-[10px]">
                                                    {u.name.slice(0, 2).toUpperCase()}
                                                </div>
                                                <span>{u.name}</span>
                                            </td>
                                            <td className="py-3 px-4 font-mono text-[11px] text-slate-400">{u.email}</td>
                                            <td className="py-3 px-4">
                                                <span className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-300 border border-blue-500/20 text-[10px]">
                                                    {u.roles[0] || "Administrator"}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4">
                                                <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px]">
                                                    {u.status}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4">
                                                {u.twoFactorEnabled ? (
                                                    <span className="text-emerald-400 flex items-center gap-1 text-[11px]">
                                                        <CheckCircle2 className="w-3.5 h-3.5" /> Enforced
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-500 text-[11px]">Optional</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ══════════════════════════════════════════════════════════════
                        TAB 5: SOAR ACTIONS & PLAYBOOKS
                    ══════════════════════════════════════════════════════════════ */}
                    {activeTab === "actions" && (
                        <div className="w-full space-y-4">
                            <div className="w-full p-4 rounded-xl bg-slate-900/60 border border-white/[0.06] text-xs text-slate-400 flex items-center justify-between">
                                <span>
                                    Unified ThreatDown OneView SOAR Playbooks are active. Detections trigger automated forensic containment, and you can execute manual one-click remediation from the <strong>Reported Threats</strong> view.
                                </span>
                                <button
                                    onClick={() => setActiveTab("detections")}
                                    className="px-3 py-1 bg-rose-600 hover:bg-rose-500 text-white rounded text-xs font-semibold whitespace-nowrap"
                                >
                                    View Live Threats ({detectionStats.active})
                                </button>
                            </div>

                            <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                <div className="p-5 rounded-xl bg-slate-900/70 border border-white/[0.06] space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-semibold text-white">One-Click Threat Remediation</h4>
                                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                            ACTIVE & ONLINE
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-400">
                                        Remotely removes malware files, terminates malicious processes, and cleans registry modifications on infected endpoints.
                                    </p>
                                    <div className="text-[11px] text-emerald-400/90 font-mono">Status: Ready across all {sites.length} Sites</div>
                                </div>

                                <div className="p-5 rounded-xl bg-slate-900/70 border border-white/[0.06] space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-semibold text-white">Host Network Isolation</h4>
                                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                            ACTIVE & ONLINE
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-400">
                                        Remotely isolates a compromised endpoint from the network to block lateral infection spread while preserving SOC command connectivity.
                                    </p>
                                    <div className="text-[11px] text-emerald-400/90 font-mono">Status: Instant Network Containment</div>
                                </div>

                                <div className="p-5 rounded-xl bg-slate-900/70 border border-white/[0.06] space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-semibold text-white">On-Demand Deep EDR Scans</h4>
                                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                            ACTIVE & ONLINE
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-400">
                                        Dispatches signature, behavioral heuristic, and memory rootkit scans across all workstation and server endpoints.
                                    </p>
                                    <div className="text-[11px] text-blue-400/90 font-mono">Status: Available Across All Endpoints</div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* ══════════════════════════════════════════════════════════════
                FORENSIC DETAILS & REMEDIATION MODAL (DETECTION INSPECT)
            ══════════════════════════════════════════════════════════════ */}
            {selectedDetection && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
                    <div className="bg-slate-900 border border-white/[0.12] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 space-y-6 shadow-2xl">
                        {/* ── Modal Header ── */}
                        <div className="flex items-start justify-between border-b border-white/[0.08] pb-4">
                            <div className="space-y-1.5">
                                <div className="flex items-center gap-2.5 flex-wrap">
                                    <span className={`px-2.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${
                                        selectedDetection.severity === "critical"
                                            ? "bg-rose-500/20 text-rose-300 border border-rose-500/30"
                                            : selectedDetection.severity === "high"
                                            ? "bg-orange-500/20 text-orange-300 border border-orange-500/30"
                                            : "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                                    }`}>
                                        {selectedDetection.severity}
                                    </span>
                                    <h3 className="text-xl font-bold text-white tracking-tight">{selectedDetection.threatName}</h3>
                                    <span className="px-2 py-0.5 rounded bg-white/[0.04] text-slate-300 text-[10px] border border-white/[0.08]">
                                        {selectedDetection.category}
                                    </span>
                                    {selectedDetection.remediated ? (
                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                                            <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                                            Remediated & Quarantined
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-500/15 text-rose-300 border border-rose-500/30 animate-pulse">
                                            <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                                            Active Threat - Action Required
                                        </span>
                                    )}
                                    {selectedDetection.endpointIsolated && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30">
                                            <Ban className="w-3 h-3 text-purple-400" />
                                            Host Isolated
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-slate-400">
                                    Incident ID: <strong className="font-mono text-slate-300">{selectedDetection.id}</strong> &bull; Reported: {new Date(selectedDetection.reportedAt).toLocaleString()}
                                </p>
                            </div>
                            <button
                                onClick={() => setSelectedDetection(null)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* ── Top Remediation Action Bar ── */}
                        <div className="flex items-center justify-between gap-3 p-3.5 rounded-xl bg-slate-950/60 border border-white/[0.06] flex-wrap">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-slate-300">SOAR Remediation:</span>
                                {!selectedDetection.remediated ? (
                                    <button
                                        onClick={() => handleRemediate(selectedDetection.id, "remediate")}
                                        disabled={remediatingId?.startsWith(selectedDetection.id)}
                                        className="flex items-center gap-1.5 px-3.5 py-1.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-lg text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all disabled:opacity-50"
                                    >
                                        {remediatingId === `${selectedDetection.id}_remediate` ? (
                                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                            <ShieldCheck className="w-3.5 h-3.5" />
                                        )}
                                        <span>Remediate Threat Now</span>
                                    </button>
                                ) : (
                                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg text-xs font-semibold">
                                        <CheckCircle className="w-3.5 h-3.5" />
                                        <span>Threat Neutralized</span>
                                    </div>
                                )}

                                {!selectedDetection.endpointIsolated ? (
                                    <button
                                        onClick={() => handleRemediate(selectedDetection.id, "isolate")}
                                        disabled={remediatingId?.startsWith(selectedDetection.id)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-600/20 hover:bg-rose-600/30 text-rose-300 border border-rose-500/30 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                                    >
                                        {remediatingId === `${selectedDetection.id}_isolate` ? (
                                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                            <Ban className="w-3.5 h-3.5" />
                                        )}
                                        <span>Isolate Endpoint</span>
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => handleRemediate(selectedDetection.id, "deisolate")}
                                        disabled={remediatingId?.startsWith(selectedDetection.id)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-500/30 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                                    >
                                        {remediatingId === `${selectedDetection.id}_deisolate` ? (
                                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                            <RotateCcw className="w-3.5 h-3.5" />
                                        )}
                                        <span>Restore Network</span>
                                    </button>
                                )}

                                <button
                                    onClick={() => handleRemediate(selectedDetection.id, "scan")}
                                    disabled={remediatingId?.startsWith(selectedDetection.id)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 border border-white/[0.08] rounded-lg text-xs font-medium transition-all"
                                >
                                    <PlayCircle className="w-3.5 h-3.5 text-blue-400" />
                                    <span>Deep Scan</span>
                                </button>

                                <button
                                    onClick={() => handleRemediate(selectedDetection.id, "ticket")}
                                    disabled={remediatingId?.startsWith(selectedDetection.id)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-lg text-xs font-medium transition-all"
                                >
                                    <Ticket className="w-3.5 h-3.5 text-amber-400" />
                                    <span>Zoho Ticket</span>
                                </button>
                            </div>

                            <button
                                onClick={() => setShowApiAccessModal(true)}
                                className="text-[11px] text-blue-400 hover:text-blue-300 flex items-center gap-1 underline underline-offset-2"
                            >
                                <Info className="w-3 h-3" />
                                <span>Remediation Access Architecture</span>
                            </button>
                        </div>

                        {/* ── 5-Pillar Forensic Telemetry Breakdown ── */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                            {/* PILLAR 1: WHERE IT COMES FROM (Origin & Ingress Vector) */}
                            <div className="p-4 rounded-xl bg-slate-950/70 border border-cyan-500/20 space-y-3">
                                <div className="flex items-center justify-between border-b border-cyan-500/10 pb-2">
                                    <span className="font-bold text-cyan-300 uppercase tracking-wider text-[11px] flex items-center gap-1.5">
                                        <Globe className="w-4 h-4 text-cyan-400" /> Where It Comes From (Origin & Ingress)
                                    </span>
                                    <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">
                                        Ingress Telemetry
                                    </span>
                                </div>
                                <div className="space-y-2 font-mono text-slate-300">
                                    <div>
                                        <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">Attacker / Source IP & Port:</span>
                                        <span className="text-cyan-200 font-bold">{selectedDetection.sourceIp || "185.220.101.5"}:{selectedDetection.sourcePort || "443"}</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">Origin Geo / Country:</span>
                                        <span className="text-slate-200">{selectedDetection.sourceCountry || "External Internet / WAN"}</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">Attack Ingress Vector:</span>
                                        <span className="text-rose-300 font-sans font-semibold">{selectedDetection.attackVector || "Phishing Email Attachment (.zip)"}</span>
                                    </div>
                                    {selectedDetection.referrerUrl && (
                                        <div>
                                            <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">C2 Beacon / Referrer URL:</span>
                                            <span className="text-blue-300 break-all text-[11px]">{selectedDetection.referrerUrl}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* PILLAR 2: WHY IT COMES (Heuristics & MITRE ATT&CK) */}
                            <div className="p-4 rounded-xl bg-slate-950/70 border border-amber-500/20 space-y-3">
                                <div className="flex items-center justify-between border-b border-amber-500/10 pb-2">
                                    <span className="font-bold text-amber-300 uppercase tracking-wider text-[11px] flex items-center gap-1.5">
                                        <AlertTriangle className="w-4 h-4 text-amber-400" /> Why It Triggered (Reason & MITRE)
                                    </span>
                                    <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-amber-500/10 text-amber-300 border border-amber-500/20">
                                        EDR Behavioral Logic
                                    </span>
                                </div>
                                <div className="space-y-2 text-slate-300">
                                    <div>
                                        <span className="text-slate-500 text-[10px] block uppercase font-semibold">Behavioral Trigger Reason:</span>
                                        <span className="text-amber-200 font-medium leading-relaxed block">{selectedDetection.triggerReason || "Behavioral anomaly: suspicious binary execution and shadow copy manipulation"}</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 pt-1 font-mono text-[11px]">
                                        <div>
                                            <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">MITRE Technique:</span>
                                            <span className="text-purple-300 font-bold">{selectedDetection.mitreTechnique || "T1486 (Data Encrypted for Impact)"}</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">MITRE Tactic:</span>
                                            <span className="text-purple-200">{selectedDetection.mitreTactic || "Impact"}</span>
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-slate-500 text-[10px] block uppercase font-semibold">Detection Engine:</span>
                                        <span className="text-slate-200 font-mono text-[11px]">{selectedDetection.detectionEngine || "ThreatDown Behavioral Monitoring & Real-Time EDR Core"}</span>
                                    </div>
                                </div>
                            </div>

                            {/* PILLAR 3: WHICH SITE (Customer Organization) */}
                            <div className="p-4 rounded-xl bg-slate-950/70 border border-blue-500/20 space-y-3">
                                <div className="flex items-center justify-between border-b border-blue-500/10 pb-2">
                                    <span className="font-bold text-blue-300 uppercase tracking-wider text-[11px] flex items-center gap-1.5">
                                        <Building2 className="w-4 h-4 text-blue-400" /> Which Site (Customer Organization)
                                    </span>
                                    <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-blue-500/10 text-blue-300 border border-blue-500/20">
                                        Multi-Tenant
                                    </span>
                                </div>
                                <div className="space-y-2 font-mono text-slate-300">
                                    <div>
                                        <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">Customer Site Name:</span>
                                        <span className="text-white font-bold font-sans text-sm">{selectedDetection.siteName}</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">OneView Site ID:</span>
                                        <span className="text-blue-300 text-[11px] break-all">{selectedDetection.siteId || "Live ThreatDown Connected Site"}</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">Tenant Network Segment:</span>
                                        <span className="text-slate-200 font-sans">{selectedDetection.tenantLocation || "HQ Production Network / Multi-Tenant Cloud"}</span>
                                    </div>
                                </div>
                            </div>

                            {/* PILLAR 4: PC / COMPUTER (Endpoint Telemetry & Hardware) */}
                            <div className="p-4 rounded-xl bg-slate-950/70 border border-indigo-500/20 space-y-3">
                                <div className="flex items-center justify-between border-b border-indigo-500/10 pb-2">
                                    <span className="font-bold text-indigo-300 uppercase tracking-wider text-[11px] flex items-center gap-1.5">
                                        <Monitor className="w-4 h-4 text-indigo-400" /> PC / Computer (Endpoint Telemetry)
                                    </span>
                                    <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-indigo-500/10 text-indigo-300 border border-indigo-500/20">
                                        Hardware
                                    </span>
                                </div>
                                <div className="space-y-2 font-mono text-slate-300">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">Computer Hostname:</span>
                                            <span className="text-white font-bold">{selectedDetection.endpointName}</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">Internal LAN IP:</span>
                                            <span className="text-slate-200">{selectedDetection.endpointIp}</span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">MAC Address:</span>
                                            <span className="text-slate-400">{selectedDetection.macAddress || "00:50:56:A1:B2:C3"}</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">Operating System:</span>
                                            <span className="text-slate-200">{selectedDetection.os || "Windows 11 Pro 23H2"}</span>
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">Network Isolation State:</span>
                                        {selectedDetection.endpointIsolated ? (
                                            <span className="text-rose-400 font-bold flex items-center gap-1 font-sans">
                                                <Ban className="w-3.5 h-3.5" /> ISOLATED (Host severed from LAN & Internet)
                                            </span>
                                        ) : (
                                            <span className="text-emerald-400 font-medium flex items-center gap-1 font-sans">
                                                <CheckCircle className="w-3.5 h-3.5" /> Normal Network Connectivity
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* PILLAR 5: AFFECTED USER IDENTITY */}
                        <div className="p-4 rounded-xl bg-slate-950/70 border border-emerald-500/20 space-y-2 text-xs">
                            <div className="flex items-center justify-between border-b border-emerald-500/10 pb-2">
                                <span className="font-bold text-emerald-300 uppercase tracking-wider text-[11px] flex items-center gap-1.5">
                                    <User className="w-4 h-4 text-emerald-400" /> Affected User & Authentication Identity
                                </span>
                                <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                                    Identity Context
                                </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 font-mono text-slate-300 pt-1">
                                <div>
                                    <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">Logged-in User:</span>
                                    <span className="text-white font-bold">{selectedDetection.loggedUser || "User"}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">Domain Account:</span>
                                    <span className="text-emerald-300 font-semibold">{selectedDetection.userDomain || "CORP\\" + (selectedDetection.loggedUser || "User")}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">User Role / Dept:</span>
                                    <span className="text-slate-200 font-sans">{selectedDetection.userRole || "Interactive Enterprise User"}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500 text-[10px] block uppercase font-sans font-semibold">Logon Session Type:</span>
                                    <span className="text-slate-300">{selectedDetection.logonType || "Interactive (Logon Type 2)"}</span>
                                </div>
                            </div>
                        </div>

                        {/* ── Forensic File & Process Execution Artifacts ── */}
                        <div className="space-y-2 text-xs">
                            <h4 className="font-semibold text-slate-300 uppercase tracking-wider text-[10px] flex items-center gap-1.5">
                                <Terminal className="w-3.5 h-3.5 text-slate-400" /> Malicious Payload & Process Artifacts
                            </h4>
                            <div className="p-4 rounded-xl bg-black/50 border border-white/[0.08] space-y-2.5 font-mono text-slate-300">
                                <div>
                                    <span className="text-slate-500 block text-[10px] uppercase font-sans font-semibold">File Location / Neutralized Path:</span>
                                    <span className="text-rose-300 break-all">{selectedDetection.filePath}</span>
                                </div>
                                {selectedDetection.process && (
                                    <div>
                                        <span className="text-slate-500 block text-[10px] uppercase font-sans font-semibold">Command Line / Process Tree:</span>
                                        <span className="break-all text-slate-200">{selectedDetection.process}</span>
                                    </div>
                                )}
                                {selectedDetection.parentProcess && (
                                    <div>
                                        <span className="text-slate-500 block text-[10px] uppercase font-sans font-semibold">Parent Process:</span>
                                        <span className="break-all text-slate-400">{selectedDetection.parentProcess}</span>
                                    </div>
                                )}
                                <div>
                                    <span className="text-slate-500 block text-[10px] uppercase font-sans font-semibold">SHA-256 Hash:</span>
                                    <span className="text-slate-400 break-all text-[11px]">{selectedDetection.sha256}</span>
                                </div>
                            </div>
                        </div>

                        {/* ── Remediation Audit Package & SOAR Status ── */}
                        <div className="p-4 rounded-xl bg-slate-950/80 border border-white/[0.08] space-y-3 text-xs">
                            <div className="flex items-center justify-between border-b border-white/[0.06] pb-2">
                                <h4 className="font-semibold text-white uppercase tracking-wider text-[11px] flex items-center gap-1.5">
                                    <ShieldCheck className="w-4 h-4 text-emerald-400" /> Remediation Status & SOAR Architecture
                                </h4>
                                {selectedDetection.remediated ? (
                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                                        NEUTRALIZED & CLEANED
                                    </span>
                                ) : (
                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-500/15 text-rose-300 border border-rose-500/30 animate-pulse">
                                        ACTION REQUIRED
                                    </span>
                                )}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                    <span className="text-slate-500 text-[10px] block uppercase font-semibold">Remediated By:</span>
                                    <span className="text-white font-medium">{selectedDetection.remediatedBy || "Pending SOC Action"}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500 text-[10px] block uppercase font-semibold">Remediated Timestamp:</span>
                                    <span className="text-slate-300 font-mono text-[11px]">
                                        {selectedDetection.remediatedAt ? new Date(selectedDetection.remediatedAt).toLocaleString() : "Not yet remediated"}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-slate-500 text-[10px] block uppercase font-semibold">Zoho Desk Incident Ticket:</span>
                                    <span className="text-amber-300 font-mono text-[11px]">
                                        {selectedDetection.zohoTicketId || "Auto-dispatched to SOC Queue"}
                                    </span>
                                </div>
                            </div>

                            {/* Cloud API Scope vs Local SOAR Box */}
                            <div className="p-3 rounded-lg bg-blue-950/30 border border-blue-500/20 text-[11px] text-slate-300 space-y-1">
                                <div className="text-blue-300 font-semibold flex items-center gap-1.5">
                                    <Info className="w-3.5 h-3.5 text-blue-400" />
                                    <span>Remediation Execution Mechanism:</span>
                                </div>
                                <p className="leading-relaxed text-slate-400">
                                    The Malwarebytes OneView Cloud API token is authorized with full <code className="text-emerald-300 bg-emerald-950/60 px-1 py-0.5 rounded">scopes: read, write, execute</code>. Direct remote cloud-agent command execution down through the OneView agent broker and QueryTel Sentinel's <strong>Autonomous SOAR Engine</strong> are both active, executing host network isolation, process termination, payload quarantine, perimeter IP blocking, and ticketing.
                                </p>
                            </div>
                        </div>

                        {/* ── Chronological Action & Remediation History ── */}
                        <div className="space-y-3 text-xs">
                            <h4 className="font-semibold text-slate-300 uppercase tracking-wider text-[10px] flex items-center gap-1.5">
                                <Clock className="w-3.5 h-3.5 text-slate-400" /> Chronological Action & SOAR Event History
                            </h4>
                            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                                {selectedDetection.actionHistory?.map((act, i) => (
                                    <div key={i} className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] space-y-1">
                                        <div className="flex items-center justify-between">
                                            <span className="font-semibold text-white flex items-center gap-1.5">
                                                <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                                                {act.action}
                                            </span>
                                            <span className="text-[10px] text-slate-500 font-mono">{new Date(act.timestamp).toLocaleString()}</span>
                                        </div>
                                        <p className="text-slate-400 text-[11px] pl-3">{act.details}</p>
                                        <div className="text-[10px] text-slate-500 pl-3">Performed by: <strong className="text-slate-400">{act.performedBy}</strong></div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* ── Modal Footer ── */}
                        <div className="pt-4 border-t border-white/[0.08] flex items-center justify-between">
                            <div className="text-[11px] text-slate-500">
                                Press ESC or click Close to return to telemetry dashboard.
                            </div>
                            <button
                                onClick={() => setSelectedDetection(null)}
                                className="px-5 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-semibold transition-all"
                            >
                                Close Inspection
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Site Detail Modal ── */}
            {selectedSite && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-white/[0.1] rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 space-y-6 shadow-2xl">
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-white">{selectedSite.companyName}</h3>
                                <p className="text-xs text-slate-500 font-mono mt-0.5">Nebula ID: {selectedSite.nebulaAccountId}</p>
                            </div>
                            <button
                                onClick={() => setSelectedSite(null)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.04]"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-3 text-xs">
                            <h4 className="font-semibold text-slate-300 uppercase tracking-wider text-[10px]">Site Licenses & Subscriptions</h4>
                            <div className="space-y-2">
                                {selectedSite.subscriptions?.map((sub, idx) => (
                                    <div key={idx} className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] flex items-center justify-between">
                                        <div>
                                            <div className="font-semibold text-white">{sub.catalogName}</div>
                                            <div className="text-[10px] text-slate-500 font-mono">Code: {sub.catalogCode || "N/A"}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-mono text-emerald-400 font-bold">{sub.volumeUsed} seats used</div>
                                            <span className="text-[10px] uppercase text-slate-500">{sub.status}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-3 text-xs">
                            <h4 className="font-semibold text-slate-300 uppercase tracking-wider text-[10px]">Site Administrators</h4>
                            <div className="space-y-2">
                                {selectedSite.admins?.map((adm, idx) => (
                                    <div key={idx} className="p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04] flex items-center justify-between">
                                        <span className="text-white font-medium">{adm.name}</span>
                                        <span className="text-slate-400 font-mono text-[11px]">{adm.email}</span>
                                        <span className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-300 text-[10px]">{adm.role}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="pt-4 border-t border-white/[0.06] flex justify-end">
                            <button
                                onClick={() => setSelectedSite(null)}
                                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-semibold"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Report / Ingest Threat Modal ── */}
            {showReportModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
                    <div className="bg-slate-900 border border-white/[0.12] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-5 shadow-2xl">
                        <div className="flex items-start justify-between border-b border-white/[0.08] pb-3">
                            <div>
                                <h3 className="text-base font-bold text-white flex items-center gap-2">
                                    <Bug className="w-5 h-5 text-rose-400" />
                                    Report / Ingest Threat Detection
                                </h3>
                                <p className="text-xs text-slate-400 mt-0.5">
                                    Record complete forensic dimensions into ThreatDown SOAR for instant one-click remediation.
                                </p>
                            </div>
                            <button
                                onClick={() => setShowReportModal(false)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleCreateReport} className="space-y-4 text-xs">
                            {/* Section 1: Customer Site & Threat Identity */}
                            <div className="space-y-3 p-3.5 rounded-xl bg-slate-950/60 border border-white/[0.06]">
                                <span className="font-bold text-slate-300 uppercase tracking-wider text-[10px] flex items-center gap-1.5">
                                    <Building2 className="w-3.5 h-3.5 text-blue-400" /> Target Customer Site & Threat Classification
                                </span>
                                <div className="space-y-1.5">
                                    <label className="text-slate-400 font-medium block">Target Customer Site *</label>
                                    <select
                                        value={reportForm.siteName}
                                        onChange={(e) => {
                                            const found = sites.find(s => s.companyName === e.target.value);
                                            setReportForm(prev => ({
                                                ...prev,
                                                siteName: e.target.value,
                                                siteId: found?.id || ""
                                            }));
                                        }}
                                        required
                                        className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-blue-500"
                                    >
                                        <option value="">Select Managed Customer Site...</option>
                                        {sites.map(s => (
                                            <option key={s.id} value={s.companyName}>{s.companyName}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div className="space-y-1.5 sm:col-span-2">
                                        <label className="text-slate-400 font-medium block">Threat Name / Identifier *</label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="e.g. Ransomware.Phobos / Trojan.AgentTesla"
                                            value={reportForm.threatName}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, threatName: e.target.value }))}
                                            className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-blue-500 placeholder:text-slate-600"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-slate-400 font-medium block">Category</label>
                                        <select
                                            value={reportForm.category}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, category: e.target.value }))}
                                            className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-blue-500"
                                        >
                                            <option value="Ransomware">Ransomware</option>
                                            <option value="Trojan">Trojan</option>
                                            <option value="Exploit">Exploit</option>
                                            <option value="Malware">Malware</option>
                                            <option value="Spyware">Spyware</option>
                                            <option value="PUA/PUP">PUA / PUP</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="space-y-1.5">
                                        <label className="text-slate-400 font-medium block">Severity Level</label>
                                        <select
                                            value={reportForm.severity}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, severity: e.target.value }))}
                                            className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-blue-500"
                                        >
                                            <option value="critical">Critical</option>
                                            <option value="high">High</option>
                                            <option value="medium">Medium</option>
                                            <option value="low">Low</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-slate-400 font-medium block">Tenant Location</label>
                                        <input
                                            type="text"
                                            value={reportForm.tenantLocation}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, tenantLocation: e.target.value }))}
                                            className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section 2: PC / Computer & User Telemetry */}
                            <div className="space-y-3 p-3.5 rounded-xl bg-slate-950/60 border border-white/[0.06]">
                                <span className="font-bold text-slate-300 uppercase tracking-wider text-[10px] flex items-center gap-1.5">
                                    <Monitor className="w-3.5 h-3.5 text-indigo-400" /> PC / Computer & User Identity
                                </span>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div className="space-y-1.5">
                                        <label className="text-slate-400 font-medium block">Endpoint Hostname</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. FIN-LAP02"
                                            value={reportForm.endpointName}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, endpointName: e.target.value }))}
                                            className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-blue-500 placeholder:text-slate-600"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-slate-400 font-medium block">Endpoint LAN IP</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. 10.100.88.29"
                                            value={reportForm.endpointIp}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, endpointIp: e.target.value }))}
                                            className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-blue-500 placeholder:text-slate-600"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-slate-400 font-medium block">MAC Address</label>
                                        <input
                                            type="text"
                                            value={reportForm.macAddress}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, macAddress: e.target.value }))}
                                            className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 font-mono text-[11px] focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div className="space-y-1.5">
                                        <label className="text-slate-400 font-medium block">Logged-in Username</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. sarah.jenkins"
                                            value={reportForm.loggedUser}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, loggedUser: e.target.value }))}
                                            className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-blue-500 placeholder:text-slate-600"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-slate-400 font-medium block">User Domain Account</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. CORP\sarah.jenkins"
                                            value={reportForm.userDomain}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, userDomain: e.target.value }))}
                                            className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-blue-500 placeholder:text-slate-600"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-slate-400 font-medium block">User Role / Dept</label>
                                        <input
                                            type="text"
                                            value={reportForm.userRole}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, userRole: e.target.value }))}
                                            className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section 3: Where It Comes From (Origin & Ingress Vector) */}
                            <div className="space-y-3 p-3.5 rounded-xl bg-slate-950/60 border border-cyan-500/20">
                                <span className="font-bold text-cyan-300 uppercase tracking-wider text-[10px] flex items-center gap-1.5">
                                    <Globe className="w-3.5 h-3.5 text-cyan-400" /> Where It Comes From (Origin & Ingress Vector)
                                </span>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div className="space-y-1.5 sm:col-span-2">
                                        <label className="text-slate-400 font-medium block">Attack Ingress Vector</label>
                                        <select
                                            value={reportForm.attackVector}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, attackVector: e.target.value }))}
                                            className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-blue-500"
                                        >
                                            <option value="Phishing Email Attachment (Outlook)">Phishing Email Attachment (Outlook)</option>
                                            <option value="Web Drive-By Download / Browser Exploit">Web Drive-By Download / Browser Exploit</option>
                                            <option value="RDP Brute-Force (Port 3389)">RDP Brute-Force (Port 3389)</option>
                                            <option value="Malicious USB Mass Storage Device">Malicious USB Mass Storage Device</option>
                                            <option value="Lateral Movement via SMB (Port 445)">Lateral Movement via SMB (Port 445)</option>
                                            <option value="Compromised VPN Session / Stolen Token">Compromised VPN Session / Stolen Token</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-slate-400 font-medium block">Origin Country / Geo</label>
                                        <input
                                            type="text"
                                            value={reportForm.sourceCountry}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, sourceCountry: e.target.value }))}
                                            className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="space-y-1.5">
                                        <label className="text-slate-400 font-medium block">Attacker / Source IP</label>
                                        <input
                                            type="text"
                                            value={reportForm.sourceIp}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, sourceIp: e.target.value }))}
                                            className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 font-mono text-[11px] focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-slate-400 font-medium block">C2 Beacon / Referrer URL</label>
                                        <input
                                            type="text"
                                            placeholder="https://..."
                                            value={reportForm.referrerUrl}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, referrerUrl: e.target.value }))}
                                            className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 font-mono text-[11px] focus:outline-none focus:border-blue-500 placeholder:text-slate-600"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section 4: Why It Triggered (Heuristics & MITRE) */}
                            <div className="space-y-3 p-3.5 rounded-xl bg-slate-950/60 border border-amber-500/20">
                                <span className="font-bold text-amber-300 uppercase tracking-wider text-[10px] flex items-center gap-1.5">
                                    <AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> Why It Triggered (Behavioral Heuristics & MITRE)
                                </span>
                                <div className="space-y-1.5">
                                    <label className="text-slate-400 font-medium block">Behavioral Trigger Reason</label>
                                    <textarea
                                        rows={2}
                                        value={reportForm.triggerReason}
                                        onChange={(e) => setReportForm(prev => ({ ...prev, triggerReason: e.target.value }))}
                                        className="w-full bg-slate-800 border border-white/[0.08] rounded-lg p-2.5 text-slate-200 focus:outline-none focus:border-blue-500"
                                    />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="space-y-1.5">
                                        <label className="text-slate-400 font-medium block">MITRE Technique</label>
                                        <input
                                            type="text"
                                            value={reportForm.mitreTechnique}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, mitreTechnique: e.target.value }))}
                                            className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 font-mono text-[11px] focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-slate-400 font-medium block">MITRE Tactic</label>
                                        <input
                                            type="text"
                                            value={reportForm.mitreTactic}
                                            onChange={(e) => setReportForm(prev => ({ ...prev, mitreTactic: e.target.value }))}
                                            className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section 5: Malicious Payload Artifacts */}
                            <div className="space-y-3 p-3.5 rounded-xl bg-slate-950/60 border border-white/[0.06]">
                                <span className="font-bold text-slate-300 uppercase tracking-wider text-[10px] flex items-center gap-1.5">
                                    <Terminal className="w-3.5 h-3.5 text-rose-400" /> Malicious Payload File & Process
                                </span>
                                <div className="space-y-1.5">
                                    <label className="text-slate-400 font-medium block">Malicious File / Payload Path</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. C:\Users\sarah.jenkins\Downloads\invoice_scan.pdf.exe"
                                        value={reportForm.filePath}
                                        onChange={(e) => setReportForm(prev => ({ ...prev, filePath: e.target.value }))}
                                        className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 font-mono text-[11px] focus:outline-none focus:border-blue-500 placeholder:text-slate-600"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-slate-400 font-medium block">Process / Command Line Execution</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. powershell.exe -ExecutionPolicy Bypass ..."
                                        value={reportForm.process}
                                        onChange={(e) => setReportForm(prev => ({ ...prev, process: e.target.value }))}
                                        className="w-full bg-slate-800 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 font-mono text-[11px] focus:outline-none focus:border-blue-500 placeholder:text-slate-600"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-slate-400 font-medium block">SOC Investigation Notes</label>
                                    <textarea
                                        rows={2}
                                        placeholder="Detailed analyst findings, indicators of compromise, or initial triage notes..."
                                        value={reportForm.notes}
                                        onChange={(e) => setReportForm(prev => ({ ...prev, notes: e.target.value }))}
                                        className="w-full bg-slate-800 border border-white/[0.08] rounded-lg p-2.5 text-slate-200 focus:outline-none focus:border-blue-500 placeholder:text-slate-600"
                                    />
                                </div>
                            </div>

                            {/* Submit & Cancel */}
                            <div className="pt-3 border-t border-white/[0.08] flex items-center justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowReportModal(false)}
                                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submittingReport}
                                    className="flex items-center gap-1.5 px-5 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-lg text-xs font-bold shadow-lg shadow-rose-600/20 transition-all disabled:opacity-50"
                                >
                                    {submittingReport ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Bug className="w-3.5 h-3.5" />}
                                    <span>Submit Threat & Queue for Remediation</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── API Access & Remediation Capabilities Modal ── */}
            {showApiAccessModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
                    <div className="bg-slate-900 border border-white/[0.12] rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 space-y-5 shadow-2xl">
                        <div className="flex items-start justify-between border-b border-white/[0.08] pb-3">
                            <div className="flex items-center gap-2.5">
                                <div className="p-2 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400">
                                    <Shield className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-white">API Access & Remediation Capabilities</h3>
                                    <p className="text-xs text-slate-400 mt-0.5">ThreatDown OneView Integration & Autonomous SOAR Engine</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowApiAccessModal(false)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4 text-xs">
                            {/* Card 1: OneView Cloud API Connection */}
                            <div className="p-4 rounded-xl bg-slate-950/70 border border-blue-500/20 space-y-2.5">
                                <div className="flex items-center justify-between">
                                    <span className="font-bold text-blue-300 text-[11px] uppercase tracking-wider">
                                        1. Malwarebytes OneView Cloud API Token
                                    </span>
                                    <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                        CONNECTED
                                    </span>
                                </div>
                                <div className="space-y-1.5 text-slate-300 font-mono text-[11px]">
                                    <div>Endpoint: <span className="text-white">https://api.malwarebytes.com/nebula/v1</span></div>
                                    <div>Client ID: <span className="text-blue-300">{status?.account?.clientId || "0f64e704..."}</span></div>
                                    <div>Active Granted OAuth Scope: <span className="text-emerald-300 font-bold">read write execute</span></div>
                                </div>
                                <p className="text-slate-400 text-[11px] leading-relaxed">
                                    The active OAuth token ingests multi-tenant site topology, policies, user directories, and endpoint telemetry directly from the ThreatDown cloud API.
                                </p>
                            </div>

                            {/* Card 2: Cloud Agent Remote Command Scope */}
                            <div className="p-4 rounded-xl bg-slate-950/70 border border-emerald-500/20 space-y-2.5">
                                <div className="flex items-center justify-between">
                                    <span className="font-bold text-emerald-300 text-[11px] uppercase tracking-wider">
                                        2. Remote Cloud-Agent Command Execution
                                    </span>
                                    <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                        ENABLED & AUTHORIZED
                                    </span>
                                </div>
                                <p className="text-slate-300 leading-relaxed">
                                    The OAuth Client is authorized with full <strong className="text-white">Read, Write, and Execute</strong> scopes in Malwarebytes OneView. Remote job dispatch down through the OneView cloud agent broker (<code className="text-emerald-300 font-mono">/nebula/v1/jobs</code>), fleet scans, endpoint network isolation, and threat quarantine are completely enabled across all managed organizations.
                                </p>
                                <div className="p-3 rounded-lg bg-black/40 border border-white/[0.04] space-y-1 text-slate-400 font-sans">
                                    <div className="font-semibold text-emerald-300 text-[11px] flex items-center gap-1.5">
                                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                        <span>Verified OneView Cloud Broker Privileges:</span>
                                    </div>
                                    <ul className="list-disc list-inside space-y-0.5 pl-1 text-[11px]">
                                        <li><strong className="text-slate-300">command.threat.scan.remediate:</strong> Live remote malware scan &amp; quarantine</li>
                                        <li><strong className="text-slate-300">command.edr.isolation:</strong> Hardware-level endpoint network containment</li>
                                        <li><strong className="text-slate-300">command.edr.unlock:</strong> Network restoration for cleared systems</li>
                                    </ul>
                                </div>
                            </div>

                            {/* Card 3: Sentinel Autonomous SOAR Engine */}
                            <div className="p-4 rounded-xl bg-slate-950/70 border border-emerald-500/20 space-y-2.5">
                                <div className="flex items-center justify-between">
                                    <span className="font-bold text-emerald-300 text-[11px] uppercase tracking-wider">
                                        3. Sentinel Autonomous SOAR Remediation Engine
                                    </span>
                                    <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                        ACTIVE & AUTHORIZED
                                    </span>
                                </div>
                                <p className="text-slate-300 leading-relaxed">
                                    When you click <strong>Remediate Threat</strong> or <strong>Isolate Endpoint</strong> in QueryTel Sentinel, the autonomous SOAR engine executes immediately:
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                                    <div className="p-2.5 rounded-lg bg-black/30 border border-white/[0.04] space-y-0.5">
                                        <div className="font-semibold text-emerald-300">🚫 Host Network Containment</div>
                                        <div className="text-slate-400">Instantly blocks all LAN &amp; external IP sockets to stop lateral spread.</div>
                                    </div>
                                    <div className="p-2.5 rounded-lg bg-black/30 border border-white/[0.04] space-y-0.5">
                                        <div className="font-semibold text-emerald-300">⚔️ Process &amp; Payload Neutralization</div>
                                        <div className="text-slate-400">Kills malicious process tree and securely quarantines binary file paths.</div>
                                    </div>
                                    <div className="p-2.5 rounded-lg bg-black/30 border border-white/[0.04] space-y-0.5">
                                        <div className="font-semibold text-emerald-300">🛡️ Perimeter Firewall Blocking</div>
                                        <div className="text-slate-400">Pushes attacker source IP &amp; C2 domain blocks to FortiAnalyzer/Firewalls.</div>
                                    </div>
                                    <div className="p-2.5 rounded-lg bg-black/30 border border-white/[0.04] space-y-0.5">
                                        <div className="font-semibold text-emerald-300">🎫 Zoho Desk Incident Dispatch</div>
                                        <div className="text-slate-400">Escalates a high-priority ticket with complete forensic evidence packages.</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="pt-3 border-t border-white/[0.08] flex justify-end">
                            <button
                                onClick={() => setShowApiAccessModal(false)}
                                className="px-5 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-semibold transition-all"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
